import Overlay from './Overlay.js';
import { el } from '../dom.js';

// Attribution for every third-party asset the sim ships. `RESOURCES/CREDITS.txt`
// is the authoring copy — keep the two in step.
//
// A section is either a list of `entries` (a two-column table) or a flat list of
// `tools` (a wrapped chip list). An entry names what the asset *is* in the sim
// and carries one or more `sources`; a source is `{ text, href, license,
// licenseHref }`, where `text` is the credit line as it should read — the
// original work's title and its author, which the Creative Commons licences
// require alongside a link to the licence itself.

const ERIC_PARKER = 'https://www.linkedin.com/in/eric-hellcats-parker/details/experience/';
const PORTFOLIO = 'https://jeff-beene.com';

const CC_BY = {
  license: 'CC BY 4.0',
  licenseHref: 'http://creativecommons.org/licenses/by/4.0/',
};
const CC_BY_NC = {
  license: 'CC BY-NC 4.0',
  licenseHref: 'http://creativecommons.org/licenses/by-nc/4.0/',
};

const SECTIONS = [
  {
    title: '3D Assets',
    entries: [
      {
        name: 'A-10A Thunderbolt II',
        sources: [{
          text: 'MilitaryAssets on CGTrader',
          href: 'https://www.cgtrader.com/designers/militaryassets',
        }],
      },
      {
        name: 'Mi-8 helicopter',
        sources: [{
          text: '"helicopter mi-8" by mamont nikita',
          href: 'https://skfb.ly/oJWYQ',
          ...CC_BY,
        }],
      },
      {
        name: 'MiG-21',
        sources: [{
          text: '"MIG-21 Fishbed — cold war era fighter" by NETRUNNER_pl',
          href: 'https://skfb.ly/6SoVo',
          ...CC_BY,
        }],
      },
      {
        name: 'ZHUK BR-3',
        sources: [{
          text: '"ZHUK BR-3 (Soviet)" by 42manako',
          href: 'https://skfb.ly/pwtyv',
          ...CC_BY,
        }],
      },
      {
        name: '2K22 Tunguska',
        sources: [{
          text: '"2K22 Tunguska (SA-19 Grison)" by 42manako',
          href: 'https://skfb.ly/pFwoB',
          ...CC_BY_NC,
        }],
      },
    ],
  },
  {
    title: 'Cockpit Textures',
    entries: [
      { name: 'Cockpit layout illustration', sources: [{ text: 'Alberto Salomon' }] },
      { name: '"Horse of War" DCS texture mod', sources: [{ text: 'RICARDO' }] },
    ],
  },
  {
    title: 'Music',
    entries: [
      {
        name: 'Start screen theme',
        sources: [
          {
            text: '"La Defaite" by Gerald Olivieri — Uppbeat',
            href: 'https://uppbeat.io/t/gerald-olivieri/la-defaite',
          },
          { text: 'FUVQYMGFLDNHCJRN' },
        ],
      },
      {
        name: 'Intro theme',
        sources: [{
          text: 'JaroslawKh — Pond5',
          href: 'https://www.pond5.com/ru/artist/jaroslawkh',
        }],
      },
    ],
  },
  {
    title: 'Audio',
    entries: [
      {
        name: 'Engine and airframe',
        sources: [{
          text: 'A-10 Warthog Sound FX Library',
          href: 'https://soundfx.studio/shop/sfx-for-games/aviation/a-10-warthog-sound-fx-library/',
        }],
      },
      {
        name: 'Other sound effects',
        sources: [{ text: 'Various contributors on freesound.org', href: 'https://freesound.org/' }],
      },
    ],
  },
  {
    title: 'Other Assets',
    entries: [
      {
        name: 'HUD font',
        sources: [{
          text: 'F/A-18 Hornet display font by 0x408',
          href: 'https://github.com/0x408/hornet-display',
        }],
      },
      { name: 'Satellite imagery', sources: [{ text: 'ArcGIS Online / Microsoft Bing' }] },
      {
        name: 'Terrain heightmaps',
        sources: [{
          text: 'Tangram Heightmapper',
          href: 'https://tangrams.github.io/heightmapper/',
        }],
      },
      {
        name: 'Building data',
        sources: [
          { text: 'OpenStreetMap', href: 'https://www.openstreetmap.org/copyright' },
          { text: 'BlenderGIS', href: 'https://github.com/domlysz/BlenderGIS' },
        ],
      },
      {
        name: 'Explosion footage',
        sources: [{ text: 'ProductionCrate', href: 'https://www.productioncrate.com/' }],
      },
      {
        name: 'HUD icons',
        sources: [
          {
            text: 'Ammunition icons by Muhammad_Usman — Flaticon',
            href: 'https://www.flaticon.com/free-icons/ammunition',
          },
          {
            text: 'Skull icons by meaicon — Flaticon',
            href: 'https://www.flaticon.com/free-icons/skull',
          },
          {
            text: 'Bullseye icons by Nixxdsgn — Flaticon',
            href: 'https://www.flaticon.com/free-icons/bullseye',
          },
        ],
      },
    ],
  },
  {
    title: 'Built With',
    tools: [
      'Three.js',
      'Anthropic Claude (Opus 4.6 · Opus 5)',
      'Blender',
      'Audacity',
      'Photoshop',
      'Illustrator',
    ],
  },
];

/** External link — opened in a new tab so the sim is never navigated away from. */
function link(text, href) {
  const a = el('a', 'credits-link', text);
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  return a;
}

/** Credits — a short about note, then third-party attribution by category. */
export default class CreditsOverlay extends Overlay {
  constructor({ onClose }) {
    super({ title: 'CREDITS', onClose });

    this.body.appendChild(this._buildAbout());
    // this.body.appendChild(this._buildLede());
    for (const section of SECTIONS) this.body.appendChild(this._buildSection(section));
  }

  _buildAbout() {
    const about = el('div', 'credits-lede');
    const intro = el('p');
    intro.append(
      el('span', 'credits-lede-em', 'A-10A Warthog'),
      ' is a free, browser-based flight sim inspired by the combat sims of the late ’90s and '
      + 'early 2000s: a love letter to the A-10 Thunderbolt II as it nears retirement after 50 years '
      + 'of service, set in a 1:1 recreation of the Barry M. Goldwater Range near Yuma, Arizona.',
    );
    const signoff = el('p', 'credits-signoff');
    signoff.append('Made with love by ', link('Jeff Beene', PORTFOLIO));
    about.append(intro, signoff);
    return about;
  }

  _buildLede() {
    const lede = el('p', 'credits-lede');
    lede.append(
      el('span', 'credits-lede-em', 'A-10 Cuba!'),
      ' is why I love aviation and flight sims. I flew it into the ground more times than I can '
      + 'count as a kid, and this whole project is me chasing that feeling again. Thanks to ',
      link('Eric Parker', ERIC_PARKER),
      ', the programmer behind it.',
    );
    return lede;
  }

  _buildSection({ title, entries, tools }) {
    const group = el('div', 'credits-group');
    group.appendChild(el('div', 'credits-group-title', title));
    group.appendChild(tools ? this._buildTools(tools) : this._buildTable(entries));
    return group;
  }

  _buildTable(entries) {
    const table = el('div', 'credits-table');
    for (const { name, sources } of entries) {
      const row = el('div', 'credits-row');
      row.appendChild(el('span', 'credits-item', name));

      const col = el('span', 'credits-source');
      for (const src of sources) {
        const line = el('span', 'credits-source-line');
        line.appendChild(src.href ? link(src.text, src.href) : el('span', null, src.text));
        if (src.license) {
          line.appendChild(el('span', 'credits-sep', '·'));
          const licence = link(src.license, src.licenseHref);
          licence.classList.add('credits-license');
          line.appendChild(licence);
        }
        col.appendChild(line);
      }
      row.appendChild(col);
      table.appendChild(row);
    }
    return table;
  }

  _buildTools(tools) {
    const list = el('div', 'credits-tools');
    for (const tool of tools) list.appendChild(el('span', 'credits-tool', tool));
    return list;
  }
}
